import PropTypes from "prop-types";
import { Box, Button, Chip, Grid, Paper, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import dayjs from "dayjs";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import NoDataAvailable from "../../../components/NoDataAvailable/NoDataAvailable";
import Loader from "../../../components/Loader/Loader";
import FormResponseTable from "./FormResponseTable";
import UserTableAvatar from "../../../components/UserTableAvatar/UserTableAvatar";
import FormResponseFilterDrawer from "./FormResponseFilterDrawer";

import { clearFormResponse, fetchFormResponse } from "../../../store/formResponseSlice";
import { HelpTexts } from "../../../constants/constants";
import { getStatus, calculateFilterCount } from "../../../utils/utils";
import {
  download_active_icon,
  download_disabled_icon,
  filter_active_icon
} from "../../../constants/icons";
import IconButtons from "../../../components/IconButtons/IconButtons";
import { backendBaseUrl } from "../../../config/config";
import { apiBasePath } from "../../../constants/constants";
import { ROLE_TYPES, font_16, font_15 } from "../../../utils/utils";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

dayjs.extend(utc);
dayjs.extend(tz);

const { NON_ACTIVE_ATTENDEE } = HelpTexts;
const initialFilter = {
  status: null,
};

function prepareFormStatus(statusList) {
  if (!Array.isArray(statusList)) return [];
  return statusList.map((el) => ({ id: el.key, name: el.value }));
}

const status = {
  statuses: [
    { id: 0, title: "Active", value: "ACTIVE", variant: "GREEN-TEXT" },
    { id: 1, title: "Inactive", value: "INACTIVE", variant: "RED-TEXT" },
    { id: 3, title: "Completed", value: "COMPLETED", variant: "GREEN-TEXT" },
    { id: 4, title: "Pending", value: "PENDING", variant: "RED-TEXT" },
  ],
  getVariant: (value) =>
    status.statuses.find((it) => it.value === value)?.variant || "",
  getTitle: (value) =>
    status.statuses.find((it) => it.value === value)?.title || "",
};

const getScoreClass = (val) => {
  const score = parseFloat(val);
  if (isNaN(score)) return null;
  if (score >= 0 && score <= 2.5) return "score-chip chip-red";
  if (score > 2.5 && score < 3.8) return "score-chip chip-yellow";
  if (score >= 3.8) return "score-chip chip-green";
  return "score-chip chip-default";
};

const formatDate = (date) => {
  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) return "-";
  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const ExpandableList = ({ items }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!Array.isArray(items) || items.length === 0) return "-";

  const initialCount = 2;
  const shouldShowAction = items.length > initialCount;
  const displayItems = isExpanded ? items : items.slice(0, initialCount);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.2rem", py: 0.5 }}>
      {displayItems.map((item, idx) => (
        <Typography key={`${item}-${idx}`} variant="font_12">
          {item}
        </Typography>
      ))}

      {shouldShowAction && (
        <Box
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            mt: 0.5
          }}
        >
          <Typography
            variant="font_9_bold"
            color="secondary"
            sx={{ fontWeight: 600, textTransform: "uppercase" }}
          >
            {isExpanded ? "VIEW LESS" : "VIEW MORE"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const extractUserFields = (user = {}) => ({
  newerId: user.employeeCode || "",
  fullName: user.fullName || "",
  profilePictureUrl: user.profilePictureUrl || "",
  newerEmail: user.email || "",
  userDeleted: user.deleted || false,
  competency: user.competency?.name || "",
  employeeStatus: user.deleted ? "INACTIVE" : "ACTIVE",
});


const extractMentorFields = (mentor = {}) => ({
  mentorFullName: mentor.fullName || "",
  mentorEmail: mentor.email || "",
  mentorProfilePictureUrl: mentor.profilePictureUrl || "",
  mentorDeleted: mentor.deleted || false,
  mentorId: mentor.employeeCode,
});


const extractReportingManagerFields = (reportingManager = {}) => ({
  reportingManagerFullName: reportingManager.fullName || "",
  reportingManagerEmail: reportingManager.email || "",
  reportingManagerProfilePictureUrl: reportingManager.profilePictureUrl || "",
  reportingManagerDeleted: reportingManager.deleted || false,
  reportingManagerId: reportingManager.employeeCode,
});

const extractEvaluatedByFields = (evaluatedby = {}) => ({
  evaluatedby: evaluatedby?.fullName || "",
  evaluatedbyEmail: evaluatedby?.email || "",
  evaluatedbyProfilePictureUrl: evaluatedby?.profilePictureUrl || "",
  evaluatedbyDeleted: evaluatedby?.deleted || false,
  evaluatedbyId: evaluatedby?.employeeCode,
});

const extractUserData = (user = {}) => {
  const mentor = user.mentor || {};
  const reportingManager = user.reportingManager || {};

  return {
    ...extractUserFields(user),
    ...extractMentorFields(mentor),
    ...extractReportingManagerFields(reportingManager),
  };
};

const extractAssessmentData = (item = {}) => ({
  dateOfAssessment: formatDate(item.trigger?.assessmentDate),
  assessmentPeriod: `${formatDate(item.trigger?.periodFrom)} - ${formatDate(
    item.trigger?.periodTo
  )}`,
  assessmentName: item.trigger?.assessmentName,
  bootcamp: item.trigger?.bootcamp.name,
  status: item.status,
});

const buildDynamicColumnsData = (inputs = []) =>
  inputs.reduce((acc, input) => {
    const label = input?.input?.label || "Unknown";
    const type = input?.input?.type;
    const rawValue = input?.value || [];

    const isMultiSelect = ["PROJECT_NAME", "PERFORMANCE_ASSESSED_BY"].includes(type);

    acc[label] = {
      value: isMultiSelect ? rawValue : rawValue.join(", "),
      tooltip: input?.input?.description || "",
    };
    return acc;
  }, {});

const mapApiToTableRows = (apiResponse = []) =>
  apiResponse.map((item) => ({
    ...extractUserData(item.filledForUser),
    ...extractAssessmentData(item),
    ...buildDynamicColumnsData(item.inputResponses?.results),
    ...extractEvaluatedByFields(item.filledByUser)
  }));


const extractDynamicColumnsFromItem = (item, seenKeys) => {
  const inputs = item.inputResponses?.results || [];
  const newColumns = [];

  inputs.forEach((input) => {
    const label = input?.input?.label || "Unknown";
    const type = input?.input?.type;

    if (seenKeys.has(label)) return;
    seenKeys.add(label);

    newColumns.push({
      key: label,
      label,
      tooltip: input?.input?.description || "",
      render: (cell) => {
        const val = cell?.value;
        if (type === "AVERAGE_SCORE" && val && val !== "-") {
          const className = getScoreClass(val);
          if (className) {
            return (
              <Chip
                label={val}
                size="small"
                className={className}
              />
            );
          }
        }

        const isMultiLineType = type === "PROJECT_NAME" || type === "PERFORMANCE_ASSESSED_BY";
        if (isMultiLineType) {
          return <ExpandableList items={val} />;
        }

        return val || "-";
      },
    });
  });

  return newColumns;
};
const generateColumns = (formType, data, attendeeStatuses) => {
  const staticColumns = getColumnsByType(formType, attendeeStatuses);
  const seenKeys = new Set(staticColumns.map((col) => col.key));
  const dynamicColumns = [];

  data.forEach((item) => {
    const newColumns = extractDynamicColumnsFromItem(item, seenKeys);
    dynamicColumns.push(...newColumns);
  });

  return [...staticColumns, ...dynamicColumns];
};


const createNameEmailColumn = (attendeeStatuses) => ({
  key: "fullName",
  label: "Name",
  render: (value, row) => (
    <UserTableAvatar
      imgAlt={row.fullName}
      imgSrc={row.profilePictureUrl}
      href={row.newerId}
      label={row.fullName}
      subLabel={row.newerEmail}
      deleted={row.userDeleted}
      userId={row.newerId}
      tooltip={
        row.userDeleted
          ? NON_ACTIVE_ATTENDEE(
            getStatus(attendeeStatuses, row.attendeeStatus),
            row.statusUpdatedDate
              ? dayjs(row.statusUpdatedDate)
                .tz("Asia/Kolkata")
                .format("DD MMM YYYY")
              : "-"
          )
          : null
      }
    />
  ),
});


const createSimpleNameEmailColumn = () => ({
  key: "fullName",
  label: "Name",
  render: (value, row) => (
    <UserTableAvatar
      imgAlt={row.fullName}
      imgSrc={row.profilePictureUrl}
      href={row.newerId}
      label={row.fullName}
      subLabel={row.newerEmail}
      deleted={row.userDeleted}
      userId={row.newerId}
    />
  ),
});


const createMentorEmailColumn = (label = "Mentor") => ({
  key: "mentorEmail",
  label: label,
  render: (value, row) => (
    <UserTableAvatar
      imgAlt={row.mentorFullName}
      imgSrc={row.mentorProfilePictureUrl}
      href={row.mentorId}
      label={row.mentorFullName}
      subLabel={row.mentorEmail}
      deleted={row.mentorDeleted}
      userId={row.mentorId}
    />
  ),
});
const createEvaluatedByEmailColumn = () => ({
  key: "evaluatedBy",
  label: "Evaluated By",
  render: (value, row) => (
    <UserTableAvatar
      imgAlt={row.evaluatedby}
      imgSrc={row.evaluatedbyProfilePictureUrl}
      href={row.evaluatedbyId}
      label={row.evaluatedby}
      subLabel={row.evaluatedbyEmail}
      deleted={row.evaluatedbyDeleted}
      userId={row.evaluatedbyId}
    />
  ),
});

const createReportingManagerColumn = () => ({
  key: "reportingManagerEmail",
  label: "Reporting Manager",
  render: (value, row) => (
    <UserTableAvatar
      imgAlt={row.reportingManagerFullName}
      imgSrc={row.reportingManagerProfilePictureUrl}
      href={row.reportingManagerId}
      label={row.reportingManagerFullName}
      subLabel={row.reportingManagerEmail}
      deleted={row.reportingManagerDeleted}
      userId={row.reportingManagerId}
    />
  ),
});

const createStatusColumn = (key, label) => ({
  key: key,
  label: label,
  minWidth: 150, maxWidth: 150,
  render: (value) => (
    <Box className="empoyee-status">
      <Chip
        className="same-size-chip"
        variant={status.getVariant(value)}
        label={status.getTitle(value)}
      />
    </Box>
  ),
});

const getClassroomAssessmentPerformanceColumns = (attendeeStatuses) => [
  createNameEmailColumn(attendeeStatuses),
  createMentorEmailColumn("Bootcamp Mentor"),
  createReportingManagerColumn(),
  createEvaluatedByEmailColumn(),
  createStatusColumn("status", "Status"),
  { key: "competency", label: "Competency" },
  { key: "bootcamp", label: "Bootcamp" },
  { key: "assessmentName", label: "Assessment Name" },
  { key: "dateOfAssessment", label: "Date of Assessment" },
];

const getClassroomBehaviourColumns = () => [
  createSimpleNameEmailColumn(),
  createMentorEmailColumn(),
  createReportingManagerColumn(),
  createEvaluatedByEmailColumn(),
  createStatusColumn("status", "Status"),
  { key: "competency", label: "Competency" },
  { key: "bootcamp", label: "Bootcamp" },
  { key: "assessmentPeriod", label: "Assessment Period" }
];

const getShadowProjectAssessmentPerformanceColumns = () => [
  createSimpleNameEmailColumn(),
  createMentorEmailColumn(),
  createReportingManagerColumn(),
  createEvaluatedByEmailColumn(),
  createStatusColumn("status", "Status"),
  { key: "competency", label: "Competency" },
  { key: "bootcamp", label: "Bootcamp" },
  { key: "assessmentPeriod", label: "Assessment Period" },
];

const getShadowProjectBehaviourColumns = () => [
  createSimpleNameEmailColumn(),
  createMentorEmailColumn(),
  createReportingManagerColumn(),
  createEvaluatedByEmailColumn(),
  createStatusColumn("status", "Status"),
  { key: "competency", label: "Competency" },
  { key: "bootcamp", label: "Bootcamp" },
  { key: "assessmentPeriod", label: "Assessment Period" },
];

const getDefaultColumns = () => [
  { key: "label", label: "Label" },
  { key: "value", label: "Value" },
];

const getColumnsByType = (formType, attendeeStatuses) => {
  switch (formType) {
    case "CLASSROOM_ASSESSMENT_PERFORMANCE":
      return getClassroomAssessmentPerformanceColumns(attendeeStatuses);
    case "CLASSROOM_BEHAVIOUR":
      return getClassroomBehaviourColumns();
    case "SHADOW_PROJECT_ASSESSMENT_PERFORMANCE":
      return getShadowProjectAssessmentPerformanceColumns();
    case "SHADOW_PROJECT_BEHAVIOUR":
      return getShadowProjectBehaviourColumns();
    default:
      return getDefaultColumns();
  }
};

function FormResponse({ selectedResponseForm, selectedTriggerId, formType, onBack }) {
  const dispatch = useDispatch();
  const isInitialized = useRef(false);

  const {
    list: apiData = [],
    loading = false,
    totalResults = 0,
  } = useSelector((state) => state.formResponse || {});
  const attendeeStatuses = useSelector(
    (state) => state?.utility?.constants?.attendeeStatuses
  );

  const formStatusList = useSelector((state) => state.utility.constants?.formStatus);
  const security = useSelector((state) => state?.security);
  const roles = security?.roles;
  const isHr = ROLE_TYPES.isHr(roles);

  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);

  const [openFilter, setOpenFilter] = useState(false);
  const [isFilter, setIsFilter] = useState(initialFilter);
  const [statusOptions, setStatusOptions] = useState([]);

  const filterCount = useMemo(() => calculateFilterCount(isFilter), [isFilter]);
  const filterActivated = filterCount > 0;
  const applyFilter = useCallback(
    (filter = isFilter, pageOffset = offset) => {
      if (!selectedResponseForm?.id) return;

      dispatch(clearFormResponse());
      dispatch(
        fetchFormResponse({
          formId: selectedResponseForm.id,
          status: filter?.status?.id || null,
          triggerId: selectedTriggerId || null,
          offset: pageOffset,
        })
      );
    },
    [dispatch, isFilter, offset, selectedResponseForm?.id, selectedTriggerId]
  );

  const handleFilterChange = (name, value) => {
    setIsFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterSubmit = () => {
    setOffset(0);
    setCurrentPage(1);
    setOpenFilter(false);
    applyFilter(isFilter, 0);
  };

  const handleFilterRemove = () => {
    const resetFilter = initialFilter;
    setIsFilter(resetFilter);
    setOffset(0);
    setCurrentPage(1);
    setOpenFilter(false);
    applyFilter(resetFilter, 0);
  };

  const handlePageChange = (page) => {
    const newOffset = page - 1;
    setCurrentPage(page);
    setOffset(newOffset);
    applyFilter(isFilter, newOffset);
  };

  const handleDownloadReport = () => {
    if (selectedResponseForm?.id) {
      const fileUrl = `${backendBaseUrl}${apiBasePath}/formResponse/responseReport?formId=${selectedResponseForm.id}`;
      window.open(fileUrl, "_blank");
    }
  };

  useEffect(() => {
    if (isInitialized.current && selectedResponseForm?.id === isInitialized.current) return;

    if (selectedResponseForm?.id) {
      isInitialized.current = selectedResponseForm.id;
      applyFilter(initialFilter, 0);
    }
  }, [selectedResponseForm?.id, applyFilter]);

  useEffect(() => {
    const formStatus = prepareFormStatus(formStatusList);
    setStatusOptions(formStatus);
  }, [formStatusList]);

  useEffect(() => {
    const responseData = Array.isArray(apiData)
      ? apiData
      : apiData?.results || [];
    if (!responseData.length) {
      setRows([]);
      return;
    }
    setRows(mapApiToTableRows(responseData));
    setColumns(generateColumns(formType, responseData, attendeeStatuses));
  }, [apiData, formType, attendeeStatuses]);

  const totalPages = useMemo(
    () => Math.ceil(totalResults / 20),
    [totalResults]
  );

  useEffect(() => {
    return () => {
      dispatch(clearFormResponse());
    };
  }, [dispatch]);

 return (
    <>
      {loading && <Loader />}
      <Grid container className="main-container">
        
        <Grid 
          item 
          md={12} 
          className="header-container"
          sx={{ 
            height: "auto !important",
            minHeight: "60px"
          }}
        >
          <Box
            display="flex"
            alignItems="flex-start" 
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="font_21_bold">
                {selectedResponseForm?.name}
              </Typography>

              {selectedResponseForm?.description && (
                <Box display="flex" alignItems="flex-start" gap={0.5} sx={{ mt: 0.2 }}>
                  <InfoOutlinedIcon 
                    sx={{ 
                      fontSize: "1rem", 
                      color: "#de1186", 
                      mt: "3px",
                      flexShrink: 0
                    }} 
                  />
                  <Typography
                    variant="font_12"
                    component="div"
                    sx={{ 
                      color: "#4b4b4b", 
                      fontStyle: "italic",
                      wordBreak: "break-word",
                      "& p": { margin: 0 } 
                    }}
                    dangerouslySetInnerHTML={{
                      __html: selectedResponseForm.description,
                    }}
                  />
                </Box>
              )}
            </Box>
            <Box display="flex" alignItems="center" sx={{ alignSelf: "flex-start", gap: 1 }}>
              <Box
                className="filterButton"
                sx={{
                  border: filterActivated
                    ? "2px solid #de1186"
                    : "1px solid #de118680",
                }}
              >
                <IconButtons
                  height={font_15}
                  width={font_15}
                  image={filter_active_icon}
                  handleClick={() => setOpenFilter(true)}
                  tooltip="Add Filters"
                  count={filterCount}
                />
              </Box>

              {(isHr) && (
                <Box>
                  <IconButtons
                    size="small"
                    height={font_16}
                    width={font_16}
                    image={download_active_icon}
                    disabledImage={download_disabled_icon}
                    handleClick={handleDownloadReport}
                    tooltip="Download Form Response Report"
                    disabled={!rows.length}
                    disabledTooltip={!rows.length && "There are no responses to download"}
                  />
                </Box>
              )}
              
              <Button
                variant="outlined"
                color="secondary"
                onClick={onBack}
                sx={{ height: "30px" }}
              >
                <Typography variant="outlineBtnLabel">Back</Typography>
              </Button>
            </Box>
          </Box>
        </Grid>

        <Grid item md={12} className="body-container">
          <Grid container className="table-parent-container">
            <Paper
              elevation={0}
              className="flex-container-column topic-paper-container"
            >
              {!rows.length ? (
                <Grid container className="no-data-container">
                  <NoDataAvailable
                    imgHeight={4}
                    imgWidth={4.5}
                    message="No Data Available"
                  />
                </Grid>
              ) : (
                <FormResponseTable
                  title="Responses"
                  columns={columns}
                  rows={rows}
                  totalResult={totalResults}
                  totalPages={totalPages}
                  currentPage={currentPage}
                  handlePageChange={handlePageChange}
                  actions={[]}
                  fixedColumnsCount={0}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      <FormResponseFilterDrawer
        open={openFilter}
        onClose={() => setOpenFilter(false)}
        handleFilterChange={handleFilterChange}
        handleFilterSubmit={handleFilterSubmit}
        handleFilterRemove={handleFilterRemove}
        filterValue={isFilter}
        statusOptions={statusOptions}
      />
    </>
  );
}

FormResponse.propTypes = {
  selectedResponseForm: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  selectedTriggerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  formType: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default FormResponse;